import type {ReferralFiltersPayload} from 'src/types/referral-v2';
import type {GetReferralTrackingApiResponse} from 'src/types/referrer-portal';
import type {Agency} from 'src/api-parsers/index.types';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch, useSelector} from 'react-redux';
import {useInflight} from 'src/hooks/redux';
import debounce from 'lodash/debounce';
import useAnalyticsQuery from 'src/components/analytics/useAnalyticsQuery';
import classify from 'src/utils/classify';
import {DEFAULT_TABLE_PAGE_SIZE} from 'src/utils/referral-v2';
import {resolveDateRange} from 'src/utils/date-range';
import {
  getFormattedDateForApi,
  isReferrerPortal,
  getTableBorderRadius,
} from 'src/components/referral-v2/common';
import {selectAgencyData} from 'src/selectors/agency';
import {selectReferralRequest} from 'src/selectors/referrer-portal';
import {getHeaders} from 'src/components/referrer-portal/my-referrals/leads/leads-table-header';
import {getAllReferralsDetails} from 'src/action-creators/referrer-portal/tracking';
import TrackingTabs from 'src/components/referral-v2/tracking/tracking-tabs';
import {
  SearchInput,
  Pagination,
  Table,
  TableTopBar,
  TableBottomBar,
  SubTitleExtraSmall,
  EmptyState,
} from '@spaced-out/ui-design-system/lib/components/index';
import {
  Filter,
  AgencyAndProgramFilter,
} from 'src/components/referrer-portal/my-referrals/filters';
import css from 'src/components/referral-v2/tracking/tracking.css';


const Leads = () => {
  const dispatch = useDispatch();
  const {t, language} = useLabelTranslation();
  const loggedInAgencyData: Agency | null | undefined =
    useSelector(selectAgencyData);
  const [query] = useAnalyticsQuery();
  const {start_date = '', end_date = ''} = resolveDateRange(query);
  const start_date_formatted_date = getFormattedDateForApi(start_date);
  const end_date_formatted_date = getFormattedDateForApi(end_date);
  const isReferrerPortalEnv = isReferrerPortal();

  const defaultFilters = [
    {
      key: 'timeframe',
      values: {
        start_date: start_date_formatted_date,
        end_date: end_date_formatted_date,
      },
    },
  ];

  const [searchText, setSearchText] = React.useState('');
  const [agencyId, setAgencyId] = React.useState(
    loggedInAgencyData?.id ?? null,
  );
  const [programId, setProgramId] = React.useState<any>(null);
  const [pageNumber, setPageNumber] = React.useState(1);
  const [filters, setFilters] =
    React.useState<ReferralFiltersPayload[]>(defaultFilters);

  const callApi = (value: string) => {
    const filtersData = [...filters];
    if (value) {
      filtersData.push({key: 'name', value});
    }
    if (agencyId) {
      filtersData.push({key: 'agency_id', value: agencyId});
    }
    if (programId) {
      filtersData.push({key: 'program_id', value: programId});
    }
    const filtersPayload = {
      filters: filtersData,
      limit: DEFAULT_TABLE_PAGE_SIZE,
      offset: (pageNumber - 1) * DEFAULT_TABLE_PAGE_SIZE,
    } as const;
    dispatch(getAllReferralsDetails(filtersPayload, true));
  };

  const debounceFn = React.useCallback(
    debounce(callApi, 500, {
      trailing: true,
    }),
    [],
  );

  const handlePageChange = (value: number | null | undefined) => {
    if (!value) {
      return;
    }
    setPageNumber(value);
  };

  const handleClear = () => {
    setPageNumber(1);
    setSearchText('');
  };

  React.useEffect(() => {
    callApi(searchText);
  }, [pageNumber, filters, programId, agencyId]);

  // @ts-expect-error - TS2322 - Type 'import("/Users/diwaker/SpacedOut/ui-server/src/types/referral-v2").GetReferralTrackingApiResponse' is not assignable to type 'import("/Users/diwaker/SpacedOut/ui-server/src/types/referrer-portal").GetReferralTrackingApiResponse'.
  const leadsDetails: GetReferralTrackingApiResponse | null | undefined =
    useSelector(selectReferralRequest);

  const isReferralsDataLoading = useInflight(
    getAllReferralsDetails(
      {
        filters: defaultFilters,
        limit: DEFAULT_TABLE_PAGE_SIZE,
        offset: (pageNumber - 1) * DEFAULT_TABLE_PAGE_SIZE,
      },
      true,
    ),
  );

  const onAgencyAndProgramFilterChange = (
    filterName: string,
    value: string | null | undefined,
  ) => {
    if (filterName === 'agency_id') {
      // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'SetStateAction<string>'.
      setAgencyId(value);
    } else {
      setProgramId(value);
    }
    handleClear();
  };

  const handleApplyFilter = (filters: Array<ReferralFiltersPayload>) => {
    setFilters([...filters]);
    handleClear();
  };

  return (
    <div
      className={classify({
        [css.referralPortaltrackingContainer]: isReferrerPortalEnv,
        [css.trackingContainer]: !isReferrerPortalEnv,
      })}
    >
      <TableTopBar>
        <div className={css.defaultGap}>
          {!isReferrerPortalEnv && <TrackingTabs />}
          <SearchInput
            onClear={() => {
              setSearchText('');
              callApi('');
            }}
            value={searchText}
            placeholder={t(
              'Search_by_referral_name',
              'Search by referral name',
            )}
            classNames={{wrapper: css.trackingSearch}}
            onChange={(e) => {
              setSearchText(e.target.value);
              debounceFn(e.target.value);
            }}
          />
          <Filter
            from="leads"
            searchText={searchText}
            handleApplyFilter={handleApplyFilter}
          />
        </div>
        <AgencyAndProgramFilter
          agencyId={agencyId}
          programId={programId}
          onChangeFilter={onAgencyAndProgramFilterChange}
        />
      </TableTopBar>
      <Table
        entries={leadsDetails ? leadsDetails.tracking : []}
        stickyHeader={isReferralsDataLoading}
        isLoading={isReferralsDataLoading}
        headers={getHeaders(
          leadsDetails ? leadsDetails.file_container : '',
          filters,
          t,
          language,
        )}
        borderRadius={getTableBorderRadius(leadsDetails?.total_pages)}
        classNames={{
          wrapper: css.tableWrapper,
        }}
        emptyText={
          <EmptyState
            description={
              searchText
                ? `${t(
                    'Doublecheck_your_spelling_or_try_using_different_keywords',
                    'Double-check your spelling or try using different keywords',
                  )}.`
                : `${t(
                    'When_candidates_make_requests_through_social_media_the_list_will_be_displayed_here',
                    'When candidates make requests through social media, the list will be displayed here',
                  )}.`
            }
            imageVariant="data"
            title={
              searchText
                ? t('No_results_found', 'No results found')
                : t('No_referral_request', 'No referral request')
            }
            classNames={{wrapper: css.emptyTableContainer}}
          />
        }
        idName="request_id"
      />
      {leadsDetails && leadsDetails.total_pages > 1 && (
        <TableBottomBar>
          <Pagination
            currentPage={pageNumber}
            onChange={handlePageChange}
            totalPages={leadsDetails.total_pages}
          >
            <SubTitleExtraSmall color="secondary">
              {`${t('Showing_page', 'Showing page')} `}
              {pageNumber} {t('of', 'of')} {leadsDetails.total_pages}
            </SubTitleExtraSmall>
          </Pagination>
        </TableBottomBar>
      )}
    </div>
  );
};

export default Leads;
